import { BotTemplateData, LanguageNameSpaces } from "@shared/types";
import Text from "common/Typography/Text";
import React, { CSSProperties, useContext } from "react";
import styled, { useTheme } from "styled-components";
import NewLookPublicBotCard from "features/bots/NewLook/NewLookPublicBotCard";
import NewLookPublicBotRow from "features/bots/PublicBots/NewLookPublicBotRow.tsx";
import Card from "common/Boxes/Card";
import LineChart from "common/Charts/LineChart/LineChart";
import Tag from "common/Tags/Tag";
import PublicBotStatistics from "features/bots/PublicBots/PublicBotStatistics";
import Button from "common/Buttons/Button";
import Flex from "common/Boxes/Flex";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import PublicBotCardMainInfo from "features/bots/BotForm/General/PublicBotCardMainInfo";
import { getBotTagsData } from "@shared/features/bots/utils";
import { typography } from "@shared/common/Themes/typography";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import PublicBotDrawer from "features/bots/PublicBots/PublicBotDrawer.tsx";
import usePublicBotCard from "features/bots/PublicBots/usePublicBotCard.ts";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import { hexToRGB } from "@shared/utils/misc.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { PUBLIC_BOT_CARD_ID } from "testIds.ts";

interface BotTemplateCardProps {
  botData: BotTemplateData;
  id?: string;
  onBotClick?: () => void;
  prepareAccountOnClick?: boolean;
  cardStyle?: CSSProperties;
  botRank?: number;
  isBotForm?: boolean;
  isApplied?: boolean;
  /** New look, bot form: a hairline above the row. */
  divided?: boolean;
  /** New look: a list row that opens the bot (the onboarding on phones). */
  asRow?: boolean;
}

/**
 * New look: outside the bot form (public bots page, onboarding) the card is
 * the new-look public bot card; inside it, a row of the form's list.
 */
const PublicBotCard = (props: BotTemplateCardProps) => {
  const { isNewLook } = useTheme();
  if (isNewLook && (props.isBotForm || props.asRow)) {
    return (
      <NewLookPublicBotRow
        botData={props.botData}
        id={props.id}
        botRank={props.botRank}
        isApplied={props.isApplied}
        divided={props.divided}
        onBotClick={props.asRow ? props.onBotClick : undefined}
        prepareAccountOnClick={props.prepareAccountOnClick}
        tile={props.asRow}
      />
    );
  }
  if (isNewLook) {
    return (
      <NewLookPublicBotCard
        botData={props.botData}
        id={props.id}
        botRank={props.botRank}
        onBotClick={props.onBotClick}
        prepareAccountOnClick={props.prepareAccountOnClick}
        cardStyle={props.cardStyle}
      />
    );
  }
  return <ClassicPublicBotCard {...props} />;
};

const ClassicPublicBotCard = ({
  botData,
  id,
  onBotClick,
  prepareAccountOnClick,
  cardStyle,
  botRank,
  isBotForm,
  isApplied,
}: BotTemplateCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isDrawer } = useContext(DrawerContext) || {};
  const {
    appTheme,
    isMobile,
    formattedChartData,
    onBotCardClick,
    onCopyClick,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    publicBotDrawerOpen,
    setPublicBotDrawerOpen,
    showNoExchangeModal,
  } = usePublicBotCard({
    botData,
    onBotClick,
    prepareAccountOnClick,
    botRank,
    isBotForm,
  });

  const getBotTags = () => {
    return getBotTagsData(botData).map((tagData) => {
      return (
        <Tag color={appTheme.colors[tagData.color] as string} key={tagData.tag}>
          {tagData?.tag.toUpperCase()}
        </Tag>
      );
    });
  };

  const mainInfo = (
    <PublicBotCardMainInfo
      botData={botData}
      name={botData.name}
      showSymbol={true}
    />
  );

  const copyButton = isApplied ? (
    <Tag
      color={appTheme.colors.primaryGrey}
      bgColor={hexToRGB(appTheme.colors.primaryGrey, 0.05)}
      style={{ whiteSpace: "nowrap", fontSize: typography.fontSizes.M }}
    >
      <CheckOutlined style={{ marginRight: 6 }} />
      {t("public_bots.copied")}
    </Tag>
  ) : (
    <Button type="primary" style={{ padding: "0 12px" }} onClick={onCopyClick}>
      {t("public_bots.copy_bot")}
    </Button>
  );

  const botDuration = (
    <Text
      type="secondary"
      $size={
        isMobile || isBotForm ? typography.fontSizes.S : typography.fontSizes.M
      }
    >
      <ClockCircleOutlined style={{ marginRight: 6 }} />
      {botData.days_running} {t("public_bots.days")}
    </Text>
  );

  const tags = <TagsWrapper>{getBotTags()}</TagsWrapper>;

  const statistics = (
    <PublicBotStatistics
      botData={botData}
      showBorder={false}
      isBotForm={isBotForm}
    />
  );

  const chart = (
    <ChartWrapper $isBotForm={isBotForm}>
      <LineChart
        data={formattedChartData}
        isMinified={true}
        style={{
          width: "100%",
          overflow: "visible",
        }}
        color={appTheme.colors.primaryBlue}
      />
    </ChartWrapper>
  );

  return (
    <>
      <StyledCard
        onClick={onBotCardClick}
        id={id}
        style={cardStyle}
        $isBotForm={isBotForm}
        $isApplied={isApplied}
        $isDrawer={isDrawer}
        data-testid={PUBLIC_BOT_CARD_ID}
      >
        <IconNameWrapper>
          {mainInfo}
          <Flex $flexDirection="column">
            {copyButton}
            {isBotForm && botDuration}
          </Flex>
        </IconNameWrapper>
        <StatsChartWrapper $isBotForm={isBotForm}>
          {statistics}
          {chart}
        </StatsChartWrapper>
        {!isBotForm && (
          <Footer>
            {tags}
            {botDuration}
          </Footer>
        )}
      </StyledCard>
      {showNoExchangeModal && (
        <NoExchangeConnectedModal
          placement="public_bot"
          onClose={() => setNoExchangeModalVisible(false)}
          action={t("public_bots.copy_a_bot")}
          open={noExchangeModalVisible}
        />
      )}
      {publicBotDrawerOpen && (
        <PublicBotDrawer
          open={publicBotDrawerOpen}
          closeDrawer={() => setPublicBotDrawerOpen(false)}
          botData={botData}
        />
      )}
    </>
  );
};

export default React.memo(PublicBotCard);

const StyledCard = styled(Card)<{
  $isBotForm?: boolean;
  $isApplied?: boolean;
  $isDrawer?: boolean;
}>`
  cursor: pointer;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  ${(p) =>
    p.$isBotForm &&
    !p.$isDrawer &&
    `
    background: ${p.theme.colors.mediumBgColor};
  `};
  ${(p) =>
    p.$isApplied &&
    `
    border: 1px solid ${p.theme.colors.primaryBlue};
  `};
`;
const IconNameWrapper = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: ${(p) => p.theme.borders.lightest};
`;
const Footer = styled(Flex)`
  margin-top: 12px;
`;
const ChartWrapper = styled.div<{ $isBotForm?: boolean }>`
  margin-top: ${(p) => (p.$isBotForm ? 0 : 12)}px;
  height: ${(p) => (p.$isBotForm ? 50 : 60)}px;
  flex: auto;
  width: ${(p) => (p.$isBotForm ? "90%" : "100%")};
`;
const TagsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 8px;
`;
const StatsChartWrapper = styled(Flex)<{ $isBotForm?: boolean }>`
  width: 100%;
  flex-direction: ${(p) => (p.$isBotForm ? "row" : "column")};
  ${(p) =>
    p.$isBotForm &&
    `
    column-gap: 16px;
  `};
`;
